import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import http, { setAccessToken } from '@/api/client'
import type { AuthPayload, Item, User, MFAChallenge, MFALoginPayload } from '@/types/api'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const permissions = ref<string[]>([])
  const initialized = ref(false)
  const authenticated = computed(() => user.value !== null)
  const isSuperAdmin = computed(() => user.value?.roles.some((role) => role.code === 'super_admin') === true)

  function apply(payload: AuthPayload) {
    setAccessToken(payload.access_token)
    localStorage.setItem('dcim.refresh_token', payload.refresh_token)
    user.value = payload.user
    permissions.value = payload.permissions
  }

  async function login(username: string, password: string) {
    const { data } = await http.post<Item<AuthPayload | MFAChallenge>>('/auth/login', { username, password })
    if ('mfa_required' in data.data && data.data.mfa_required) return data.data
    apply(data.data as AuthPayload)
    return null
  }

  async function verifyMFA(challenge_token: string, code: string) {
    const { data } = await http.post<Item<MFALoginPayload>>('/auth/mfa/verify', { challenge_token, code })
    apply(data.data)
    return data.data
  }

  async function restore() {
    if (initialized.value) return
    const refreshToken = localStorage.getItem('dcim.refresh_token')
    if (refreshToken) {
      try {
        const { data } = await http.post<Item<AuthPayload>>('/auth/refresh', { refresh_token: refreshToken })
        apply(data.data)
      } catch { clear() }
    }
    initialized.value = true
  }

  async function logout() {
    const refreshToken = localStorage.getItem('dcim.refresh_token')
    try { if (refreshToken) await http.post('/auth/logout', { refresh_token: refreshToken }) } finally { clear() }
  }

  function clear() {
    setAccessToken(null)
    localStorage.removeItem('dcim.refresh_token')
    user.value = null
    permissions.value = []
  }

  function can(permission?: string) { return !permission || permissions.value.includes(permission) }
  return { user, permissions, initialized, authenticated, isSuperAdmin, login, verifyMFA, apply, restore, logout, clear, can }
})
